import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  DefaultOptions,
  CombinedGraphQLErrors,
} from '@apollo/client'
import { Event } from '@bugsnag/js'
import { ErrorLink } from '@apollo/client/link/error'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { sha256 } from 'crypto-hash'

import bugsnagClient from '../helpers/BugsnagHelpers'
import * as config from '../configuration'

const endPoint = config.api.urlGraphQL
const FILTERED_CATEGORIES = ['validation', 'authorization']

// Apollo 4 collapsed onError's separate graphQLErrors/networkError args into a
// single `error` (a CombinedGraphQLErrors for GraphQL errors, otherwise a
// network/other error) plus `result` (the raw GraphQL result, formerly response).
const errorLink = new ErrorLink(({ error, operation, result }) => {
  if (!bugsnagClient) {
    return
  }

  if (CombinedGraphQLErrors.is(error)) {
    const filteredErrors = error.errors.filter(
      ({ extensions }) => !FILTERED_CATEGORIES.includes(extensions?.category as string)
    )

    filteredErrors.forEach(({ message, locations, path, ...additional }) => {
      const messageToSend = `[GraphQL error]: \
          Message: ${JSON.stringify(message)}, \
          Location: ${JSON.stringify(locations)}, \
          Path: ${JSON.stringify(path)}, \
          Additional: ${JSON.stringify(additional)}`

      if (config.app.APP_ENV === 'local') {
        console.error(messageToSend)
      }
      bugsnagClient?.notify(new Error(messageToSend), (event: Event) => {
        event.severity = 'error'
        event.context = 'graphQLErrors'
        event.addMetadata('metaData', {
          Message: message,
          Location: locations,
          Path: path,
          Operation: operation,
          Response: result,
          Additional: additional,
        })
      })
    })
  } else if (error) {
    if (config.app.APP_ENV === 'local') {
      console.error(`[Network error]: ${error.message}`)
    }
    bugsnagClient?.notify(new Error(`[Network error]: ${error.message}`), (event: Event) => {
      event.severity = 'error'
      event.context = 'graphQLErrors'
      event.addMetadata('metaData', {
        Operation: operation,
        Response: result,
      })
    })
  }
})

// Apollo 4 requires `errorPolicy` defaults to be declared via
// ApolloClient.DeclareDefaultOptions; it's redundant here anyway since every
// call spreads `defaultConfig` (errorPolicy: 'all') in ApiSagas.clientCall.
const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
  },
  query: {
    fetchPolicy: 'no-cache',
  },
}

const customFetch: typeof globalThis.fetch = (uri, options) => {
  const { operationName } = JSON.parse((options?.body as string) ?? '')
  // add operationName in query params to improve debugging
  return fetch(`${uri}?op=${operationName}`, options)
}

const httpLink = new HttpLink({ uri: endPoint, fetch: customFetch })

const persistedLink = createPersistedQueryLink({
  sha256,
  useGETForHashedQueries: true,
})

export const persistedClient = new ApolloClient({
  link: ApolloLink.from([persistedLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  devtools: { name: 'Apollo Persisted Client' },
})

const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, httpLink]),
  cache: new InMemoryCache(),
  devtools: { name: 'Apollo Client' },
  queryDeduplication: false,
  defaultOptions,
})

export const defaultConfig = {
  fetchPolicy: 'no-cache',
  errorPolicy: 'all',
}

export default apolloClient
